// Per-holding risk decomposition table (weight, vol, MCTR, PCTR, HRP weight).

import React from 'react';

import type { RiskDecompositionRow } from '../types';

interface Props {
    rows: RiskDecompositionRow[];
}

const pct = (v: number, digits = 1) => `${(v * 100).toFixed(digits)}%`;

export const RiskDecompositionTable: React.FC<Props> = ({ rows }) => {
    const sorted = [...rows].sort((a, b) => b.pctr - a.pctr);
    return (
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
            <h3 className="text-lg font-bold text-slate-800">Risk Decomposition (MCTR / PCTR)</h3>
            <p className="text-xs text-slate-500 font-medium mt-1 mb-4">
                Marginal contribution (MCTR): how much portfolio volatility rises per unit of extra weight.
                Percentage contribution (PCTR): each holding&apos;s share of total risk.
            </p>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                            <th className="py-2 pr-3 font-bold">Holding</th>
                            <th className="py-2 px-3 font-bold text-right">Weight</th>
                            <th className="py-2 px-3 font-bold text-right">Volatility</th>
                            <th className="py-2 px-3 font-bold text-right">MCTR</th>
                            <th className="py-2 px-3 font-bold text-right">Risk Share</th>
                            <th className="py-2 px-3 font-bold text-right">Risk / Capital</th>
                            <th className="py-2 pl-3 font-bold text-right">HRP Weight</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((r) => {
                            const leverage = r.weight > 0 ? r.pctr / r.weight : 0;
                            const leverageClass =
                                leverage > 1.25
                                    ? 'text-rose-600'
                                    : leverage < 0.75
                                      ? 'text-emerald-600'
                                      : 'text-slate-700';
                            return (
                                <tr key={r.ticker} className="border-b border-slate-50 last:border-0">
                                    <td className="py-2.5 pr-3 font-bold text-slate-800">{r.ticker}</td>
                                    <td className="py-2.5 px-3 text-right text-slate-700">{pct(r.weight)}</td>
                                    <td className="py-2.5 px-3 text-right text-slate-700">{pct(r.volatility)}</td>
                                    <td className="py-2.5 px-3 text-right text-slate-700">{pct(r.mctr, 2)}</td>
                                    <td className="py-2.5 px-3 text-right font-semibold text-slate-900">{pct(r.pctr)}</td>
                                    <td className={`py-2.5 px-3 text-right font-bold ${leverageClass}`}>
                                        {leverage.toFixed(2)}x
                                    </td>
                                    <td className="py-2.5 pl-3 text-right text-amber-600 font-semibold">{pct(r.hrp_weight)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
